/**
 * FieldDropdown
 *
 * One field's dropdown/text editor, labeled by the field. Reuses EditFieldRenderer +
 * the same option resolution (resolveFieldOptions) as the extracted-fields view, so the
 * validation popup and the evidence-modal inline editor share a single editor widget.
 */

import React, { useState } from 'react';
import { useAppSelector } from '../../../store/store';
import {
  FieldValue,
  getFieldDefinition,
  isGroupedFieldValue,
  resolveFieldOptions,
  sortOptionsSelectedFirst,
  toParentValues,
} from '../../../utils/fieldUtils';
import EditFieldRenderer from './EditFieldRenderer';

interface FieldDropdownProps {
  fieldName: string;
  value: FieldValue | undefined;
  parentValue: FieldValue | undefined;
  label: string;
  onChange: (fieldName: string, value: FieldValue) => void;
}

const FieldDropdown: React.FC<FieldDropdownProps> = ({ fieldName, value, parentValue, label, onChange }) => {
  const fieldsConfig = useAppSelector((state) => state.config.fieldsConfig);
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const [searchQuery, setSearchQuery] = useState('');

  const fieldDef = getFieldDefinition(fieldsConfig, fieldName);
  const isMultiselect = fieldDef?.type === 'multiselect';
  const isSingleSelect = fieldDef?.type === 'select';

  const selectedValues: string[] = isGroupedFieldValue(value)
    ? ([] as string[]).concat(...Object.values(value))
    : Array.isArray(value)
      ? value.filter((v) => v !== '<UNKNOWN>' && v !== '<NOT_APPLICABLE>')
      : value && value !== '<UNKNOWN>' && value !== '<NOT_APPLICABLE>'
        ? [value as string]
        : [];

  const { options: resolvedOptions, hierarchyMapping } = resolveFieldOptions(fieldDef, currentSite);
  const resolvedValues = new Set(resolvedOptions.map((o) => o.value));
  const optionsWithSelected = [
    ...resolvedOptions,
    ...selectedValues.filter((v) => !resolvedValues.has(v)).map((v) => ({ value: v, label: v })),
  ];
  const filteredOptions = optionsWithSelected.length
    ? sortOptionsSelectedFirst(
        optionsWithSelected.filter(
          (opt) =>
            opt.value !== '<UNKNOWN>' &&
            opt.value !== '<NOT_APPLICABLE>' &&
            opt.label.toLowerCase().includes(searchQuery.toLowerCase()),
        ),
        selectedValues,
      )
    : [];
  const hasNotApplicableOption = !!fieldDef?.options?.some((opt) => opt.value === '<NOT_APPLICABLE>');
  const parentFieldValues = fieldDef?.depends_on ? toParentValues(parentValue) : undefined;
  const displayValue = typeof value === 'string' && value !== '<UNKNOWN>' ? value : '';

  return (
    <div style={{ marginBottom: '10px' }}>
      <EditFieldRenderer
        fieldName={fieldName}
        value={value}
        displayValue={displayValue}
        isMultiselect={isMultiselect}
        isSingleSelect={isSingleSelect}
        hasNotApplicableOption={hasNotApplicableOption}
        filteredOptions={filteredOptions}
        hierarchyMapping={hierarchyMapping}
        parentFieldValues={parentFieldValues}
        searchQuery={searchQuery}
        label={label}
        onFieldChange={onChange}
        onSearchChange={(_f, q) => setSearchQuery(q)}
      />
    </div>
  );
};

export default FieldDropdown;
